import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import AmplifyModal from '@amplify/amplify-modal';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import ProbeDetailsPageHeader from './ProbeDetailsPageHeader';
import withAnimation from '../../../components/withAnimation/withAnimation';
import { getAssessorId } from '../../../utils/shared/probeDetailUtils';
import './ProbeDetailsPage.scss';
import { TITLE } from '../../../constants/shared/CommonConstants';

const ProbeDetailsPage = ({
    student,
    result,
    staff,
    onCloseWrappedComponent,
    body,
    assessmentDate,
    overallField,
    runningGirlsParams,
    howToUnderstandText,
    assessmentName,
    isStaffFetching,
    fetchStaff,
    helpUrl,
    invalidateResult,
    isInvalidationFeatureAvailable,
    summerPeriodLabel,
}) => {
    const assessorId = getAssessorId(result);
    const [isInvalidateModelOpen, setIsInvalidateModelOpen] = useState(false);
    const [hardCloseModal, setHardCloseModal] = useState(false);

    useEffect(() => {
        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    useEffect(() => {
        if (assessorId && !isStaffFetching && isEmpty(staff)) {
            fetchStaff(assessorId);
        }
    }, [assessorId]);

    const invalidateModelCloseHandle = useCallback(() => {
        setIsInvalidateModelOpen(false);
        clearAllBodyScrollLocks();
        // During the close modal animation,
        // there are instances when the HTML node isn't removed from the code due to an issue within @amplify/amplify-modal.
        // To avoid screen freezes, we are using conditional rendering with an additional flag
        // to forcefully remove the DOM element from the body.
        setTimeout(() => {
            const modalContainer = document.querySelector('.amp-styles.modal-open');
            if (modalContainer) {
                setHardCloseModal(true);
            }
        }, 300);
    }, []);

    const showInvalidateModal = useCallback(() => {
        setIsInvalidateModelOpen(true);
        setHardCloseModal(false);
    }, []);

    return (
        <>
            <div className="probe-redesign">
                <ProbeDetailsPageHeader
                    student={student}
                    result={result}
                    staff={staff}
                    overallField={overallField}
                    onClose={onCloseWrappedComponent}
                    assessmentDate={assessmentDate}
                    runningGirlsParams={runningGirlsParams}
                    howToUnderstandText={howToUnderstandText}
                    assessmentName={assessmentName}
                    helpUrl={helpUrl}
                    showInvalidateModal={showInvalidateModal}
                    isInvalidationFeatureAvailable={isInvalidationFeatureAvailable}
                    summerPeriodLabel={summerPeriodLabel}
                />
                {body}
                {isInvalidationFeatureAvailable && (
                    <div className="invalidate-modal-container">
                        {!hardCloseModal &&
                            createPortal(
                                <AmplifyModal
                                    fitContent
                                    aria-labelledby="invalidate-modal"
                                    onClose={invalidateModelCloseHandle}
                                    open={isInvalidateModelOpen}
                                    title="Invalidate Measure"
                                    primaryButtonText="Invalidate"
                                    secondaryButtonText="Exit"
                                    onPrimaryAction={() => {
                                        invalidateModelCloseHandle();
                                        invalidateResult(result);
                                        onCloseWrappedComponent();
                                    }}
                                    onSecondaryAction={invalidateModelCloseHandle}
                                >
                                    Assessments should only be invalidated for reasons that affect
                                    the accuracy of the result. Are you sure you want to invalidate?
                                </AmplifyModal>,
                                document.body,
                            )}
                    </div>
                )}
            </div>
        </>
    );
};

ProbeDetailsPage.defaultProps = {
    staff: {},
    runningGirlsParams: [],
    howToUnderstandText: null,
    body: null,
    assessmentDate: null,
    assessmentName: null,
    overallField: {},
    helpUrl: null,
    invalidateResult: () => {},
    isInvalidationFeatureAvailable: false,
    summerPeriodLabel: TITLE.SUMMER_TITLE,
};

ProbeDetailsPage.propTypes = {
    student: PropTypes.object.isRequired,
    result: PropTypes.object.isRequired,
    fetchStaff: PropTypes.func.isRequired,
    isStaffFetching: PropTypes.bool.isRequired,
    onCloseWrappedComponent: PropTypes.func.isRequired,
    body: PropTypes.element,
    staff: PropTypes.object,
    assessmentDate: PropTypes.string,
    runningGirlsParams: PropTypes.array,
    howToUnderstandText: PropTypes.string,
    assessmentName: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    overallField: PropTypes.object,
    helpUrl: PropTypes.string,
    invalidateResult: PropTypes.func,
    isInvalidationFeatureAvailable: PropTypes.bool,
    summerPeriodLabel: PropTypes.string,
};

export default withAnimation(ProbeDetailsPage);
